import {
  SEGMENT_FIELDS,
  getOperatorsForField,
  getValueEditorType,
  DEFAULT_TIME_WINDOW,
  DAYS_OF_WEEK,
  getFieldOptionConfig,
} from "@/constants/segmentOptions";
import {
  uid,
  ensureArray,
  parseMultiValue,
  toDisplayMultiValue,
} from "./segmentFormUtils";

const TimeWindowRule = ({ rule, onChange, onRemove }) => {
  const handleDayToggle = (day) => {
    const next = new Set(
      Array.isArray(rule.days) ? rule.days : ensureArray(rule.days),
    );
    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }
    onChange({ ...rule, days: Array.from(next) });
  };

  return (
    <div className="builder-block">
      <div className="builder-block-header">
        <span>Rule</span>
        <button
          type="button"
          className="btn-table-action danger"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
      <div className="builder-grid">
        <div className="form-group-modal">
          <label>Days</label>
          <div className="day-selector">
            {DAYS_OF_WEEK.map((day) => {
              const isActive = Array.isArray(rule.days)
                ? rule.days.includes(day.value)
                : ensureArray(rule.days).includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  className={`chip ${isActive ? "chip-active" : ""}`}
                  onClick={() => handleDayToggle(day.value)}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="form-group-modal">
          <label htmlFor={`start-${rule.id}`}>Start</label>
          <input
            id={`start-${rule.id}`}
            type="time"
            value={rule.start || "00:00"}
            onChange={(e) => onChange({ ...rule, start: e.target.value })}
          />
        </div>
        <div className="form-group-modal">
          <label htmlFor={`end-${rule.id}`}>End</label>
          <input
            id={`end-${rule.id}`}
            type="time"
            value={rule.end || "23:59"}
            onChange={(e) => onChange({ ...rule, end: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
};

export const TimeWindowEditor = ({ value, onChange }) => {
  const schedule = value || DEFAULT_TIME_WINDOW;
  const rules = (schedule.rules || []).map((rule) => ({
    id: rule.id ?? uid(),
    days: Array.isArray(rule.days) ? rule.days : ensureArray(rule.days),
    start: rule.start || "00:00",
    end: rule.end || "23:59",
  }));

  const handleRuleChange = (ruleId, nextRule) => {
    const updated = rules.map((r) => (r.id === ruleId ? nextRule : r));
    onChange({ ...schedule, rules: updated });
  };

  const handleAddRule = () => {
    const next = [
      ...rules,
      {
        id: uid(),
        days: ["mon"],
        start: "09:00",
        end: "18:00",
      },
    ];
    onChange({ ...schedule, rules: next });
  };

  const handleRemoveRule = (ruleId) => {
    const next = rules.filter((r) => r.id !== ruleId);
    onChange({ ...schedule, rules: next });
  };

  return (
    <div className="builder-block">
      <div className="form-group-modal">
        <label htmlFor="timezone">Timezone</label>
        <input
          id="timezone"
          type="text"
          value={schedule.tz || "UTC"}
          onChange={(e) => onChange({ ...schedule, tz: e.target.value })}
          placeholder="e.g. Europe/Kyiv"
        />
      </div>
      {rules.map((rule) => (
        <TimeWindowRule
          key={rule.id}
          rule={rule}
          onChange={(nextRule) => handleRuleChange(rule.id, nextRule)}
          onRemove={() => handleRemoveRule(rule.id)}
        />
      ))}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleAddRule}
      >
        + Add Time Window
      </button>
    </div>
  );
};

export const ConditionEditor = ({
  condition,
  onChange,
  onRemove,
  fieldOptionConfig = null,
}) => {
  const field =
    SEGMENT_FIELDS.find((f) => f.value === condition.field) ||
    SEGMENT_FIELDS[1];
  const operators = getOperatorsForField(field?.value);
  const operator =
    operators.find((op) => op.value === condition.op) || operators[0];
  const valueEditorType = getValueEditorType(operator);
  const optionConfig = fieldOptionConfig
    ? getFieldOptionConfig(field?.value, fieldOptionConfig)
    : getFieldOptionConfig(field?.value);

  const stringifyOption =
    optionConfig?.toString ??
    ((value) => (value === undefined || value === null ? "" : String(value)));
  const parseOption =
    optionConfig?.toRaw ??
    ((value) => (field.type === "number" ? Number(value) : value));

  const handleFieldChange = (value) => {
    const nextField = SEGMENT_FIELDS.find((f) => f.value === value) || field;
    const nextOperators = getOperatorsForField(nextField.value);
    const fallbackOperator = nextOperators[0]?.value ?? "eq";
    const nextOptionConfig = fieldOptionConfig
      ? getFieldOptionConfig(nextField.value, fieldOptionConfig)
      : getFieldOptionConfig(nextField.value);
    const nextOperator =
      nextOperators.find((op) => op.value === fallbackOperator) ||
      nextOperators[0];
    const nextValueEditorType = getValueEditorType(nextOperator);
    let nextValue;

    if (nextOptionConfig) {
      if (
        nextValueEditorType === "multi" ||
        nextOptionConfig.type === "multi"
      ) {
        nextValue = [];
      } else {
        const firstOption = nextOptionConfig.options[0];
        nextValue = firstOption
          ? nextOptionConfig.toRaw(firstOption.value)
          : nextField.type === "number"
            ? 0
            : "";
      }
    } else {
      if (nextValueEditorType === "multi") {
        nextValue = [];
      } else if (nextValueEditorType === "range") {
        nextValue = [0, 0];
      } else if (nextValueEditorType === "timeWindow") {
        nextValue = DEFAULT_TIME_WINDOW;
      } else if (nextValueEditorType === "none") {
        nextValue = undefined;
      } else {
        nextValue = nextField.type === "number" ? 0 : "";
      }
    }

    onChange({
      ...condition,
      field: nextField.value,
      op: fallbackOperator,
      value: nextValue,
      ci: nextField.type === "string" ? (condition.ci ?? true) : undefined,
    });
  };

  const handleOperatorChange = (value) => {
    const nextOperator = operators.find((op) => op.value === value) || operator;
    let nextValue = condition.value;

    switch (getValueEditorType(nextOperator)) {
      case "none":
        nextValue = undefined;
        break;
      case "range":
        nextValue = [0, 0];
        break;
      case "multi": {
        if (optionConfig) {
          nextValue = Array.isArray(condition.value) ? condition.value : [];
        } else {
          nextValue = [];
        }
        break;
      }
      case "timeWindow":
        nextValue = DEFAULT_TIME_WINDOW;
        break;
      default:
        if (optionConfig) {
          const firstOption = optionConfig.options[0];
          nextValue = firstOption
            ? optionConfig.toRaw(firstOption.value)
            : nextOperator.types.includes("number")
              ? 0
              : "";
        } else {
          nextValue = nextOperator.types.includes("number") ? 0 : "";
        }
    }

    onChange({
      ...condition,
      op: nextOperator.value,
      value: nextValue,
      ci: field.type === "string" ? (condition.ci ?? true) : undefined,
    });
  };

  const handleValueChange = (value) => {
    onChange({ ...condition, value });
  };

  const renderValueEditor = () => {
    if (valueEditorType === "none") {
      return null;
    }

    if (valueEditorType === "range") {
      const [min, max] = Array.isArray(condition.value)
        ? condition.value
        : [0, 0];
      return (
        <div className="range-inputs">
          <input
            type="number"
            value={min ?? 0}
            onChange={(e) =>
              handleValueChange([
                Number(e.target.value),
                Array.isArray(condition.value) ? condition.value[1] : 0,
              ])
            }
          />
          <span className="range-separator">to</span>
          <input
            type="number"
            value={max ?? 0}
            onChange={(e) =>
              handleValueChange([
                Array.isArray(condition.value) ? condition.value[0] : 0,
                Number(e.target.value),
              ])
            }
          />
        </div>
      );
    }

    if (valueEditorType === "multi") {
      if (optionConfig) {
        const currentValues = Array.isArray(condition.value)
          ? condition.value
          : [];
        const toggleOption = (optionValue) => {
          const stringValue = stringifyOption(optionValue);
          const exists = currentValues.some(
            (item) => stringifyOption(item) === stringValue,
          );
          const next = exists
            ? currentValues.filter(
                (item) => stringifyOption(item) !== stringValue,
              )
            : [...currentValues, optionValue];
          handleValueChange(next);
        };

        const getOptionForValue = (optionValue) => {
          const stringValue = stringifyOption(optionValue);
          return optionConfig.options.find(
            (opt) => stringifyOption(parseOption(opt.value)) === stringValue,
          );
        };

        return (
          <>
            <div className="segment-picker">
              {optionConfig.options.map((opt) => {
                const optionValue = parseOption(opt.value);
                const stringValue = stringifyOption(optionValue);
                const isChecked = currentValues.some(
                  (item) => stringifyOption(item) === stringValue,
                );
                return (
                  <label
                    key={opt.value}
                    className={`segment-picker-option ${isChecked ? "selected" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleOption(optionValue)}
                    />
                    <span className="segment-picker-label">
                      {opt.label}
                      {opt.kind && (
                        <span className={`kind-tag kind-tag-${opt.kind}`}>
                          {opt.kind}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
            {currentValues.length > 0 && (
              <div className="segment-picker-summary">
                {currentValues.map((value) => {
                  const stringValue = stringifyOption(value);
                  const match = getOptionForValue(value);
                  return (
                    <span key={stringValue} className="segment-chip">
                      {match?.label ?? stringValue}
                      {match?.kind && (
                        <span className={`kind-tag kind-tag-${match.kind}`}>
                          {match.kind}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleOption(value)}
                        aria-label="Remove value"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
          </>
        );
      }

      const isNumeric = field.type === "number";
      return (
        <input
          type="text"
          value={toDisplayMultiValue(condition.value)}
          onChange={(e) =>
            handleValueChange(
              parseMultiValue(e.target.value, isNumeric ? "number" : "string"),
            )
          }
          placeholder={
            isNumeric ? "Comma separated numbers" : "Comma separated values"
          }
        />
      );
    }

    if (valueEditorType === "timeWindow") {
      return (
        <TimeWindowEditor
          value={condition.value}
          onChange={handleValueChange}
        />
      );
    }

    if (optionConfig) {
      const currentValue = stringifyOption(condition.value);
      return (
        <select
          value={currentValue}
          onChange={(event) =>
            handleValueChange(optionConfig.toRaw(event.target.value))
          }
        >
          {optionConfig.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
              {opt.kind ? ` [${opt.kind}]` : ""}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === "boolean") {
      const current =
        condition.value === true
          ? "true"
          : condition.value === false
            ? "false"
            : "";
      return (
        <select
          value={current}
          onChange={(e) => handleValueChange(e.target.value === "true")}
        >
          <option value="">Select value</option>
          <option value="true">True</option>
          <option value="false">False</option>
        </select>
      );
    }

    const inputType = field.type === "number" ? "number" : "text";

    return (
      <input
        type={inputType}
        value={condition.value ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          if (field.type === "number") {
            handleValueChange(raw === "" ? null : Number(raw));
          } else {
            handleValueChange(raw);
          }
        }}
        placeholder={field.type === "number" ? "Enter number" : "Enter value"}
        inputMode={field.type === "number" ? "decimal" : undefined}
      />
    );
  };

  return (
    <div className="condition-card">
      <div className="condition-header">
        <h4>Condition</h4>
        <button
          type="button"
          className="btn-table-action danger"
          onClick={onRemove}
        >
          Remove
        </button>
      </div>
      <div className="condition-grid">
        <div className="form-group-modal">
          <label htmlFor={`field-${condition.id}`}>Field</label>
          <select
            id={`field-${condition.id}`}
            value={condition.field}
            onChange={(e) => handleFieldChange(e.target.value)}
          >
            {SEGMENT_FIELDS.filter((f) => !f.deprecated).map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group-modal">
          <label htmlFor={`operator-${condition.id}`}>Operator</label>
          <select
            id={`operator-${condition.id}`}
            value={condition.op}
            onChange={(e) => handleOperatorChange(e.target.value)}
          >
            {operators.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group-modal">
          <label>Value</label>
          {renderValueEditor()}
        </div>
      </div>
      {field.type === "string" && valueEditorType !== "timeWindow" && (
        <div className="condition-flags">
          <label>
            <input
              type="checkbox"
              checked={condition.ci ?? true}
              onChange={(e) => onChange({ ...condition, ci: e.target.checked })}
            />
            Case insensitive
          </label>
        </div>
      )}
      <div className="condition-flags">
        <label>
          <input
            type="checkbox"
            checked={condition.disabled ?? false}
            onChange={(e) =>
              onChange({ ...condition, disabled: e.target.checked })
            }
          />
          Disable condition
        </label>
      </div>
    </div>
  );
};

export const ConditionGroup = ({
  title,
  description,
  conditions,
  onAdd,
  onUpdate,
  onRemove,
  fieldOptionConfig = null,
}) => (
  <section className="condition-group">
    <header className="condition-group-header">
      <div>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </header>
    {conditions.length === 0 ? (
      <div className="empty-state small">
        <p>No conditions yet</p>
      </div>
    ) : (
      conditions.map((condition) => (
        <ConditionEditor
          key={condition.id}
          condition={condition}
          onChange={(next) => onUpdate(condition.id, next)}
          onRemove={() => onRemove(condition.id)}
          fieldOptionConfig={fieldOptionConfig}
        />
      ))
    )}
    <div className="condition-actions">
      <button type="button" className="btn btn-secondary" onClick={onAdd}>
        + Add condition
      </button>
    </div>
  </section>
);
